import { useContext, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import useRouter from "@stadium/hooks/lib/use-custom-route";

import { useAuth, useAuthContext } from "@stadium/services/auth/client";
import { useFlags } from "@stadium/services/launch-darkly/use-flags";

import BottomIconHideWrapper from "@stadium/shared/src/components/bottom-icon-hide-wrapper";
import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import PlacedOrderSummary from "@stadium/checkout/src/components/order-confirmation-modal";
import QRCodeSection from "@stadium/shared/qr-code-section";

import { useConfig } from "@stadium/hooks/lib/use-config";
import {
  getCurrencies,
  getPlatformFeeInfoMessage,
  getPlatformFeeLabel,
  getTaxRecoveryFeeLabel,
  getTaxRecoveryFeeRecipientInfoMessage,
} from "@stadium/redux-utils/selectors/configSelector";

import { useFetchTransactionDetails } from "@hooks/order/use-fetch-transaction-details";
import useStoreCurrencyConverter from "@hooks/currency/use-store-currency-converter";
import { CurrentUserContext } from "@contexts/current-user-provider";

import { formatPrice } from "@helpers/currency";
import { getPaymentMethod, getWalletPayments } from "@stadium/helpers/src/order-utility";
import { isCustomCurrency } from "@stadium/helpers/currency";

import { getStoreCurrencyCode, getStoreCurrencyName } from "@selectors/store";

import { DEFAULT_STC_CURRENCY } from "@stadium/constants/currency";
import { MY_WALLET } from "@stadium/constants/wallets";
import { workspaceWalletFlag } from "@stadium/services/launch-darkly/constants";

const emptyObj = {};
const emptyArr = [];

const OrganiserOrderConfirmation = ({ orderNumber, closeModal }) => {
  const { asPath, replace } = useRouter();
  const { [workspaceWalletFlag]: workspaceWalletFlagEnabled } = useFlags();
  const { isAuthenticated, isLoading } = useAuth();
  const { axiosInterceptorAdded } = useAuthContext();

  const platformFeeLabel = useConfig(getPlatformFeeLabel);
  const platformFeeTooltipText = useConfig(getPlatformFeeInfoMessage);
  const taxRecoveryFeeTooltipText = useConfig(getTaxRecoveryFeeRecipientInfoMessage);
  const taxRecoveryFeeLabel = useConfig(getTaxRecoveryFeeLabel);
  const currencies = useConfig(getCurrencies);

  const storeCurrencyCode = useSelector(getStoreCurrencyCode);
  const isCustomCurrencyShop = isCustomCurrency(storeCurrencyCode);
  const shopCurrencyName = useSelector(getStoreCurrencyName) || DEFAULT_STC_CURRENCY.label;

  const { isMobileDevice } = useContext(CurrentDeviceContext);
  const { currentUserData } = useContext(CurrentUserContext);
  const walletCurrencyCode = currentUserData?.walletCurrency?.code?.toLowerCase();

  const {
    formatPriceInDisplayStoreCurrency,
    formatPriceInGivenCurrency,
    formatPriceInStoreCurrency,
  } = useStoreCurrencyConverter();

  const { transactionDataLoading, transactionData, hasError, getTransactionDetails } =
    useFetchTransactionDetails();

  useEffect(() => {
    if (orderNumber && !isLoading && isAuthenticated && axiosInterceptorAdded) {
      getTransactionDetails(orderNumber);
    }
  }, [orderNumber, isLoading, isAuthenticated, axiosInterceptorAdded]);

  useEffect(() => {
    if (hasError) handleCloseModal();
  }, [hasError]);

  const {
    payments = emptyArr,
    cardDetails = emptyObj,
    appliedPlatformFee,
    appliedTaxRecoveryFee,
    appliedPromoTotal,
    appliedVideoMessageFee,
    organizerShare,
    billedCurrencyId,
    billingCode: checkoutNote,
    completedAt,
    invitationLink,
    invitationQrCode,
    teamName,
  } = transactionData;

  const taxRecoveryFee = +appliedTaxRecoveryFee;
  const platformFee = +appliedPlatformFee;
  const promoTotal = +appliedPromoTotal;
  const videoMessageFee = +appliedVideoMessageFee;

  const billedCurrency =
    currencies.find((currency) => currency.id === billedCurrencyId) || emptyObj;
  const isStoreCurrencySameAsUserSelectedCurrency = billedCurrency?.code === storeCurrencyCode;

  const getPriceInBilledCurrency = (price) => {
    if (isStoreCurrencySameAsUserSelectedCurrency) return "";

    return `(${formatPriceInGivenCurrency(price, storeCurrencyCode, billedCurrency.code)})`;
  };

  const { walletPayment: walletBalanceInUse, stadiumPointsPayment: pointsUsed } = getWalletPayments(
    payments,
    isCustomCurrencyShop,
  );

  const formattedWalletBalance = formatPrice(walletBalanceInUse, {
    currencyCode: walletCurrencyCode,
  });

  const totalPointsSent = organizerShare?.amount;
  const formattedTotalPoints = formatPriceInDisplayStoreCurrency(totalPointsSent);
  const totalPointsInCurrency = getPriceInBilledCurrency(totalPointsSent);

  const totalBudget =
    totalPointsSent + platformFee + taxRecoveryFee + videoMessageFee - promoTotal - pointsUsed;

  const totalBudgetInBilledCurrency = formatPriceInGivenCurrency(
    totalBudget,
    storeCurrencyCode,
    billedCurrency.code,
    { currencyDisplay: "code", formatZero: true },
  );

  const formattedPointsUsed = formatPriceInStoreCurrency(pointsUsed);
  const rewardPointsInUseInCurrency = getPriceInBilledCurrency(pointsUsed);

  const taxRecoveryFeeInStoreCurrency = formatPriceInDisplayStoreCurrency(
    taxRecoveryFee,
    storeCurrencyCode,
  );
  const taxRecoveryFeeInCurrency = getPriceInBilledCurrency(taxRecoveryFee);

  const platformFeeInStoreCurrency = formatPriceInDisplayStoreCurrency(
    platformFee,
    storeCurrencyCode,
  );
  const platformFeeInCurrency = getPriceInBilledCurrency(platformFee);

  const videoMessageFeeInStoreCurrency = formatPriceInDisplayStoreCurrency(
    videoMessageFee,
    storeCurrencyCode,
  );
  const videoMessageFeeInCurrency = getPriceInBilledCurrency(videoMessageFee);

  const promoTotalInStoreCurrency = formatPriceInDisplayStoreCurrency(
    promoTotal,
    storeCurrencyCode,
  );
  const promoTotalInCurrency = getPriceInBilledCurrency(promoTotal);

  const checkoutDetails = useMemo(() => {
    const detailsData = {
      totalSentPoints: {
        key: `Total ${shopCurrencyName} Sent`,
        value: `${formattedTotalPoints} ${totalPointsInCurrency}`,
      },
      promo: {
        key: "Promo",
        value: +promoTotal ? `- ${promoTotalInStoreCurrency} ${promoTotalInCurrency}` : null,
      },
      videoFee: {
        key: "Video Message Fee",
        value: videoMessageFee
          ? `${videoMessageFeeInStoreCurrency} ${videoMessageFeeInCurrency}`
          : null,
      },
      platformFee: {
        key: platformFeeLabel,
        value: +platformFee ? `${platformFeeInStoreCurrency} ${platformFeeInCurrency}` : null,
        tooltipContent: platformFeeTooltipText,
      },
      taxRecoveryFee: {
        key: taxRecoveryFeeLabel,
        value: +taxRecoveryFee
          ? `${taxRecoveryFeeInStoreCurrency} ${taxRecoveryFeeInCurrency}`
          : null,
        tooltipContent: taxRecoveryFeeTooltipText,
      },
      myStadiumWallet: {
        key: MY_WALLET,
        value: walletBalanceInUse ? `- ${formattedWalletBalance}` : null,
      },
      myStadiumPoints: {
        key: "My Stadium Points",
        value: pointsUsed ? `- ${formattedPointsUsed} ${rewardPointsInUseInCurrency}` : null,
      },
      teamWallet: {
        key: `(${teamName}) ${workspaceWalletFlagEnabled ? "" : "Shared "}Wallet`,
        value: teamName ? `-${totalBudgetInBilledCurrency}` : null,
      },
      paymentMethod: {
        key: "Payment Method",
        value: getPaymentMethod(payments, cardDetails),
      },
    };
    return detailsData;
  }, [
    totalPointsSent,
    walletBalanceInUse,
    pointsUsed,
    rewardPointsInUseInCurrency,
    payments,
    platformFeeInStoreCurrency,
    platformFee,
    platformFeeTooltipText,
    taxRecoveryFeeInStoreCurrency,
    taxRecoveryFee,
    taxRecoveryFeeTooltipText,
    cardDetails,
    shopCurrencyName,
    workspaceWalletFlagEnabled,
  ]);

  const orderCheckoutData = useMemo(
    () => ({
      orderNumber,
      orderCompletionDate: completedAt,
      checkoutDetails,
      totalPaid: totalBudgetInBilledCurrency,
      checkoutNote,
    }),
    [orderNumber, completedAt, checkoutDetails, totalBudgetInBilledCurrency, checkoutNote],
  );

  const handleCloseModal = () => {
    const [pathname, params] = asPath.split("?");
    const searchParams = new URLSearchParams(params || "");
    searchParams.delete("orderNumber");

    const newPath = searchParams.toString() ? `${pathname}?${searchParams.toString()}` : pathname;

    if (asPath !== newPath) {
      replace(newPath, undefined, { shallow: true });
    }
    closeModal();
  };

  const renderModalBody = () => {
    return (
      <PlacedOrderSummary
        headerTitle={`Your order #${orderNumber} has been created!`}
        summaryTableTitle="Order Summary:"
        orderDataLoading={transactionDataLoading || isLoading}
        orderData={orderCheckoutData}
        closeModalHandler={handleCloseModal}
        showChromeAppWidget
        checkoutFlowName="shops_send_points"
        renderRightSection={
          invitationLink &&
          (() => (
            <QRCodeSection
              url={invitationLink}
              id={orderNumber}
              qrCode={invitationQrCode}
              header="Share your invitation link so recipients can shop"
            />
          ))
        }
      />
    );
  };

  return isMobileDevice ? (
    <BottomIconHideWrapper>{renderModalBody()}</BottomIconHideWrapper>
  ) : (
    renderModalBody()
  );
};

export default OrganiserOrderConfirmation;
